import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  Slide,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Copy as CopyIcon,
  Twitter,
  Linkedin,
  Facebook,
  MessageCircle as WhatsApp,
  Share2 as NativeShareIcon,
} from "lucide-react";
import { useState, useEffect, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { useSnackbar } from "notistack";
import { useAuth } from "@/shared/stores/useAuth";
import useThemeStore from "@/shared/stores/useThemeStore";
import {
  buildDeckCardShareUrl,
  DECK_CARD_SHARE_ACTION_DEFAULTS,
  getDeckCardKakaoI18nKeys,
  type DeckCardKakaoKind,
  type DeckCardKakaoShareInput,
} from "@/modules/kakao";
import KakaoDeckCardShareButton from "@/shared/components/kakao-share/KakaoDeckCardShareButton";

export type DeckCardSpreadShareMeta = Pick<
  DeckCardKakaoShareInput,
  "talentName" | "cardTitle" | "coverImageUrl" | "imageUrl" | "senderName" | "thumbnailSources"
>;

interface DeckCardShareModalProps {
  open: boolean;
  onClose: () => void;
  cardKind: DeckCardKakaoKind;
  mainCardId: number | string;
  talentId?: number | string;
  demoCardId?: number | string;
  shareMeta?: DeckCardSpreadShareMeta;
}

const Transition = forwardRef(function Transition(props: any, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DeckCardShareModal = ({
  cardKind,
  mainCardId,
  talentId,
  demoCardId,
  open,
  onClose,
  shareMeta,
}: DeckCardShareModalProps) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const { darkMode } = useThemeStore();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { userInfo } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const i18nKeys = getDeckCardKakaoI18nKeys(cardKind);
  const shareActionLabel = t(i18nKeys.shareAction, {
    defaultValue: DECK_CARD_SHARE_ACTION_DEFAULTS[cardKind],
  });
  const shareText = t(i18nKeys.shareText, {
    title: shareMeta?.cardTitle ?? shareMeta?.talentName ?? "Card",
    defaultValue: "Check out this card on TaaSNet",
  });

  const coverForKakao =
    shareMeta?.coverImageUrl?.trim() || shareMeta?.imageUrl?.trim() || "";

  const kakaoShareInput: DeckCardKakaoShareInput | null =
    mainCardId != null && userInfo?.id
      ? {
          kind: cardKind,
          mainCardId,
          talentId,
          demoCardId,
          senderUserId: userInfo.id,
          senderName: shareMeta?.senderName ?? userInfo.username ?? shareMeta?.talentName ?? "",
          talentName: shareMeta?.talentName ?? userInfo.username ?? "",
          cardTitle: shareMeta?.cardTitle,
          coverImageUrl: coverForKakao,
          imageUrl: coverForKakao,
          thumbnailSources: shareMeta?.thumbnailSources,
        }
      : null;

  useEffect(() => {
    if (open && userInfo?.id) {
      const origin = window.location.origin.replace(/\/+$/, "");
      const shareableUrl = buildDeckCardShareUrl({
        kind: cardKind,
        mainCardId,
        senderUserId: userInfo.id,
        demoCardId,
        origin,
      });
      setUrl(shareableUrl);
      setTimeout(() => {
        (
          document.getElementById(`deck-card-share-link-input-${cardKind}`) as HTMLInputElement
        )?.select();
      }, 150);
    }
  }, [open, cardKind, mainCardId, demoCardId, userInfo?.id]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar(t("link_copied_to_clipboard"), { variant: "success" });
      onClose();
    } catch {
      enqueueSnackbar(t("failed_to_copy_link"), { variant: "error" });
    }
  };

  const openWindow = (shareUrl: string) =>
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=600");

  const textMuted = "var(--primary-800)";
  const iconBtnSx = {
    color: "var(--primary-800)",
    bgcolor: darkMode ? "var(--primary-200)" : "var(--primary-100)",
    border: "1px solid var(--primary-300)",
    borderRadius: "10px",
    "&:hover": {
      bgcolor: darkMode ? "var(--primary-300)" : "var(--primary-200)",
      borderColor: "var(--primary-500)",
      color: "var(--primary-1000)",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          px: isMobile ? 2 : 3,
          py: 2.5,
          bgcolor: darkMode ? "var(--primary-bg-dark)" : "var(--primary-100-light)",
          border: "1px solid var(--primary-300)",
          boxShadow: "var(--shadow-lg)",
          fontFamily: "var(--primary-font-family)",
          color: "var(--primary-1000)",
        },
      }}
    >
      <DialogTitle
        variant={isMobile ? "h6" : "h5"}
        sx={{
          fontWeight: 700,
          fontFamily: "var(--primary-font-family)",
          color: "var(--primary-1000)",
          pb: 1,
        }}
      >
        {t(i18nKeys.shareTitle, { defaultValue: "Share Card" })}
      </DialogTitle>

      <DialogContent sx={{ pt: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            mb: 1.5,
            fontFamily: "var(--primary-font-family)",
            color: textMuted,
          }}
        >
          {t("copy_the_link_scan_the_qr_code_or_share_directly")}
        </Typography>

        <TextField
          id={`deck-card-share-link-input-${cardKind}`}
          fullWidth
          value={url}
          label={t("shareable_link")}
          variant="outlined"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <Tooltip title={t("copy_link")}>
                <IconButton
                  onClick={handleCopy}
                  sx={{
                    color: "var(--primary-700)",
                    "&:hover": { bgcolor: "var(--primaryAlpha)" },
                  }}
                >
                  <CopyIcon size={18} />
                </IconButton>
              </Tooltip>
            ),
          }}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              fontFamily: "var(--primary-font-family)",
              bgcolor: darkMode ? "var(--primary-100)" : "var(--neutralWhite)",
              color: "var(--primary-1000)",
              "& fieldset": { borderColor: "var(--primary-300)" },
              "&:hover fieldset": { borderColor: "var(--primary-500)" },
              "&.Mui-focused fieldset": {
                borderColor: "var(--primary-600)",
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "var(--primary-font-family)",
              color: "var(--primary-800)",
              "&.Mui-focused": { color: "var(--primary-700)" },
            },
          }}
        />

        <Stack
          alignItems="center"
          mb={2}
          sx={{
            p: 2,
            borderRadius: "14px",
            bgcolor: darkMode ? "var(--primary-100)" : "var(--neutralWhite)",
            border: "1px solid var(--primary-300)",
          }}
        >
          <QRCodeCanvas
            value={url || " "}
            size={isMobile ? 120 : 160}
            bgColor={darkMode ? "#2a2205" : "#fcfcfc"}
            fgColor={darkMode ? "#eef5ef" : "#111111"}
          />
        </Stack>

        <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
          <Tooltip title={t("share_on_x_twitter")}>
            <IconButton
              onClick={() =>
                openWindow(
                  `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`
                )
              }
              size="small"
              sx={iconBtnSx}
            >
              <Twitter size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("share_on_linkedin")}>
            <IconButton
              onClick={() =>
                openWindow(
                  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
                )
              }
              size="small"
              sx={iconBtnSx}
            >
              <Linkedin size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("share_on_facebook")}>
            <IconButton
              onClick={() =>
                openWindow(
                  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
                )
              }
              size="small"
              sx={iconBtnSx}
            >
              <Facebook size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("share_on_whatsapp")}>
            <IconButton
              onClick={() =>
                openWindow(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`)
              }
              size="small"
              sx={iconBtnSx}
            >
              <WhatsApp size={18} />
            </IconButton>
          </Tooltip>
          {kakaoShareInput && (
            <KakaoDeckCardShareButton
              shareInput={kakaoShareInput}
              onShared={onClose}
              darkMode={darkMode}
              tooltip={t("share_on_kakao_talk")}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 0, pt: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            fontFamily: "var(--primary-font-family)",
            fontWeight: 600,
            textTransform: "none",
            color: "var(--primary-800)",
            border: "1.5px solid var(--primary-400)",
            borderRadius: "10px",
            px: 2,
            "&:hover": {
              bgcolor: "var(--primaryAlpha)",
              borderColor: "var(--primary-600)",
            },
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          startIcon={<NativeShareIcon size={18} />}
          onClick={async () => {
            if (navigator.share) {
              try {
                await navigator.share({ title: shareText, url });
                onClose();
                return;
              } catch {
                /* fall through */
              }
            }
            void handleCopy();
          }}
          variant="contained"
          sx={{
            fontFamily: "var(--primary-font-family)",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "10px",
            px: 2.25,
            bgcolor: "var(--primary-600)",
            color: darkMode ? "var(--primary-bg-dark)" : "var(--neutralWhite)",
            boxShadow: "var(--shadow-sm)",
            "&:hover": {
              bgcolor: "var(--primary-700)",
              boxShadow: "var(--shadow-md)",
            },
          }}
        >
          {shareActionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeckCardShareModal;
